import { React, useEffect, useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { REST_URI,RAZORPAY_KEY } from '../graphql/client';

const PaymentConfirmationDialog = ({ open, onClose, user, onSuccess }) => {
    const [orderId, setOrderId] = useState(null);
    const [finalAmount, setFinalAmount] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(true);
    const paymentContainer = useRef(null);
    const razorpayInstance = useRef(null);

    // Clean up function to properly close and clear Razorpay instance
    const cleanupRazorpay = () => {
        if (razorpayInstance.current) {
            razorpayInstance.current.close();
            razorpayInstance.current = null;
        }
        setOrderId(null);
        setFinalAmount(null);
    };

    // Modified close handler to clean up Razorpay
    const handleClose = () => {
        cleanupRazorpay();
        setDialogVisible(true); // Reset dialog visibility
        onClose();
        onSuccess?.();
    };

    const fetchOrder = async () => {
        try {
            // Hide the confirmation dialog once "Pay Now" is clicked
            setDialogVisible(false);
            
            const amount = 2000;
            const mobile = user.Mobile_Number;
            const response = await fetch(`${REST_URI}/create-order`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ amount, mobile }) // Send amount for the order creation
            });

            if (!response.ok) {
                throw new Error(`Server Error: ${response.status}`);
            }
            const orderData = await response.json();
            console.log("Order Data:", orderData);

            if (!orderData.orderId || !orderData.amount) {
                throw new Error("Invalid order response");
                setDialogVisible(true); // Show dialog again if error
            }
            // Set orderId and amount (in paise)
            setOrderId(orderData.orderId);
            setFinalAmount(orderData.amount);
        } catch (error) {
            console.error("Error fetching order:", error);
            alert("Failed to create order. Please try again.");
            setDialogVisible(true); // Show dialog again if there's an error
        }
    };

    useEffect(() => {
        // Only create Razorpay instance if dialog is open and we have order details
        if (open && orderId && finalAmount) {
            const mobile = localStorage.getItem("mobile") || user?.Mobile_Number;

            const options = {
                key: RAZORPAY_KEY,
                amount: finalAmount,
                currency: "INR",
                name: "TKS Trust",
                description: "UPI QR Payment",
                order_id: orderId,
                image: "/CWA_logo.png",
                theme: {
                    color: "#FFA500",
                    hide_topbar: false,
                },
                modal: {
                    backdropclose: false,
                    confirm_close: true,
                    animation: true,
                    ondismiss: () => {
                        // Handle Razorpay modal dismissal
                        setDialogVisible(true); // Show original dialog if payment is dismissed
                        cleanupRazorpay();
                    }
                },
                handler: async function (response) {
                    console.log("Payment Successful:", response);
                    localStorage.setItem("payment_completed", "true");

                    try {
                        const verifyResponse = await fetch(`${REST_URI}/verify-payment`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                paymentId: response.razorpay_payment_id,
                                orderId: orderId,
                                signature: response.razorpay_signature,
                            }),
                        });
                        const verifyData = await verifyResponse.json();
                        handleClose(); // This will close everything
                        window.location.reload();
                    } catch (error) {
                        console.error("Error verifying payment:", error);
                        setDialogVisible(true); // Show dialog again if verification fails
                    }
                },
                payment_capture: 1,
                method: {
                    upi: true,
                    netbanking: true,
                    card: true,
                },
                upi: {
                    flow: "collect",
                },
                prefill: {
                    contact: mobile,
                },
            };

            // Create new Razorpay instance
            razorpayInstance.current = new window.Razorpay(options);
            razorpayInstance.current.open();
        }
        
        // Clean up when component unmounts or dialog closes
        return () => {
            cleanupRazorpay();
        };
    }, [orderId, finalAmount, open]);

    // Only render the Dialog if dialogVisible is true
    return (
        <Dialog open={open && dialogVisible} onClose={handleClose}>
            <DialogTitle>Confirm Payment</DialogTitle>
            <DialogContent>
                <Typography>Do you want to proceed with payment for the following member?</Typography>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    <b>Name:</b> {user?.First_Name || "N/A"}<br />
                    <b>Member ID:</b> {user?.id || "N/A"}<br />
                    <b>Mobile:</b> {user?.Mobile_Number || "N/A"}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button color="success" onClick={fetchOrder}>Pay Now</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentConfirmationDialog;